import { useState } from 'react';
import { ToolLayout } from '../../components/tools/ToolLayout';
import { formatBrl } from '../../data/coins';

interface ProfitResult {
  invested: number;
  grossFinal: number;
  grossProfit: number;
  totalFees: number;
  netProfit: number;
  netReturnPct: number;
}

export const CalculadoraLucroPage: React.FC = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [buyFeePct, setBuyFeePct] = useState('0.5');
  const [sellFeePct, setSellFeePct] = useState('0.5');
  const [result, setResult] = useState<ProfitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculate = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const buy = parseFloat(buyPrice.replace(',', '.'));
    const sell = parseFloat(sellPrice.replace(',', '.'));
    const qty = parseFloat(quantity.replace(',', '.'));
    const feeBuy = parseFloat(buyFeePct.replace(',', '.')) || 0;
    const feeSell = parseFloat(sellFeePct.replace(',', '.')) || 0;

    if (![buy, sell, qty].every((v) => Number.isFinite(v) && v > 0)) {
      setError('Preencha preço de compra, preço de venda e quantidade com valores positivos.');
      return;
    }
    if (feeBuy < 0 || feeSell < 0 || feeBuy > 20 || feeSell > 20) {
      setError('As taxas devem estar entre 0% e 20%.');
      return;
    }

    const invested = buy * qty;
    const buyFee = invested * (feeBuy / 100);
    const grossFinal = sell * qty;
    const sellFee = grossFinal * (feeSell / 100);
    const totalFees = buyFee + sellFee;
    const grossProfit = grossFinal - invested;
    const netProfit = grossProfit - totalFees;
    const netReturnPct = ((grossFinal - sellFee) / (invested + buyFee) - 1) * 100;

    setResult({ invested, grossFinal, grossProfit, totalFees, netProfit, netReturnPct });
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500';

  return (
    <ToolLayout
      path="/ferramentas/calculadora-de-lucro"
      title="Calculadora de Lucro"
      metaTitle="Calculadora de Lucro com Criptomoedas (com taxas)"
      metaDescription="Calcule o lucro ou prejuízo de uma operação com criptomoedas em reais, considerando preço de compra, preço de venda, quantidade e taxas da exchange."
      intro={[
        'Saber o resultado real de uma operação exige mais do que subtrair o preço de compra do preço de venda: as taxas cobradas pela exchange na entrada e na saída reduzem o lucro — e, em operações pequenas ou de curto prazo, podem consumir boa parte dele.',
        'Esta calculadora mostra o lucro bruto, o total pago em taxas e o lucro líquido de uma operação, em reais.',
      ]}
      methodology={[
        'Valor investido = preço de compra × quantidade.',
        'Taxa de compra aplicada sobre o valor investido; taxa de venda aplicada sobre o valor de venda.',
        'Lucro líquido = (valor de venda − taxa de venda) − (valor investido + taxa de compra).',
        'Retorno % calculado sobre o total desembolsado (investimento + taxa de compra).',
        'Impostos não são considerados — no Brasil, ganhos com cripto podem ser tributáveis; consulte as regras da Receita Federal.',
      ]}
      faqs={[
        {
          question: 'Quais taxas devo informar?',
          answer:
            'As taxas de negociação da sua exchange (geralmente entre 0,1% e 0,6% por operação). Se houve taxa de saque ou depósito, você pode somá-la mentalmente ao resultado — a calculadora considera apenas as taxas percentuais de compra e venda.',
        },
        {
          question: 'A calculadora considera imposto de renda?',
          answer:
            'Não. No Brasil, vendas de criptoativos acima de determinados limites mensais podem gerar imposto sobre o ganho de capital. As regras mudam com o tempo — consulte a Receita Federal ou um contador.',
        },
        {
          question: 'Funciona para qualquer criptomoeda?',
          answer:
            'Sim. O cálculo é matemático e independe do ativo — basta informar os preços em reais e a quantidade negociada.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
        <form onSubmit={calculate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Preço de compra (R$)</span>
            <input type="number" min="0" step="any" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Preço de venda (R$)</span>
            <input type="number" min="0" step="any" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Quantidade</span>
            <input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={inputClass} required />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Taxa de compra (%)</span>
            <input type="number" min="0" max="20" step="any" value={buyFeePct} onChange={(e) => setBuyFeePct(e.target.value)} className={inputClass} />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Taxa de venda (%)</span>
            <input type="number" min="0" max="20" step="any" value={sellFeePct} onChange={(e) => setSellFeePct(e.target.value)} className={inputClass} />
          </label>
          <button type="submit" className="bg-blue-600 text-white font-semibold rounded-lg px-6 py-2.5 hover:bg-blue-700 transition">
            Calcular
          </button>
        </form>

        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>
        )}

        {result && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Valor investido', value: formatBrl(result.invested, 2) },
              { label: 'Valor de venda', value: formatBrl(result.grossFinal, 2) },
              { label: 'Lucro bruto', value: formatBrl(result.grossProfit, 2), colored: true, positive: result.grossProfit >= 0 },
              { label: 'Total em taxas', value: formatBrl(result.totalFees, 2) },
              { label: 'Lucro líquido', value: formatBrl(result.netProfit, 2), colored: true, positive: result.netProfit >= 0 },
              { label: 'Retorno líquido', value: `${result.netReturnPct >= 0 ? '+' : ''}${result.netReturnPct.toFixed(2)}%`, colored: true, positive: result.netReturnPct >= 0 },
            ].map((stat) => (
              <div key={stat.label} className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                <p className={`font-bold text-sm md:text-base ${'colored' in stat && stat.colored ? (stat.positive ? 'text-green-600' : 'text-red-600') : 'text-gray-900'}`}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </ToolLayout>
  );
};
